import { Card, Panel, Table } from '@/components/Bicester';
import Image from '@/components/Bicester/Image';
import { Form } from '@/components/formik-rsuite';
import { fetchLoseList } from '@/services/site/lAndF';
import { useRequest } from 'ahooks';
import { FormikProvider, useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { IconButton } from 'rsuite';
import { defaultLoseListInfo } from './constants';
import useTable from '@/hooks/useTable';
import { Cell, DateTimeCell, OperationCell } from '@/components/Bicester/Table/Cells';
import { getPathname, push } from '@/utils/history';
import StatusCell from '@/components/Bicester/Table/Cells/StatusCell';
import { BICESTER_FULL_DATE, BICESTER_FULL_DATE_HOURS_MINUTES } from '@/constants/dateFormat';
import useContentHeight from '@/hooks/useContentHeight';
import { LostAndFoundContext } from '.';
import PageLoader from '@/components/shared/PageLoader';
import useLoading from '@/hooks/useLoading';
import useRouter from '@/hooks/useRouter';
import _ from 'lodash';

const LostAndFoundList = () => {
  const { loseBasic, loading: loseLoading } = React.useContext(LostAndFoundContext);
  const { query } = useRouter();

  const [currentStatus, setCurrentStatus] = useState('');
  const [sortColumn, setSortColumn] = useState<string>('');
  const [orderType, setOrderType] = useState<'desc' | 'asc'>('desc');

  const {
    run: getLoseList,
    data: loseListData = {},
    loading: loseListLoading,
  } = useRequest(fetchLoseList, {
    manual: true,
  });

  const { onChange, onSearch } = useTable(async (p) => {
    await getLoseList(p);
  });

  const formik = useFormik({
    initialValues: defaultLoseListInfo,
    onSubmit(values, actions) {
      const params = Object.assign({}, _.omit(values, 'time_range'), {
        pick_start_at:
          (values?.time_range && values?.time_range?.length === 2 && values?.time_range[0]) || '',
        pick_end_at:
          (values?.time_range && values?.time_range?.length === 2 && values?.time_range[1]) || '',
      });
      onSearch(params);
      const currentStatus =
        (loseBasic?.lose_status?.find((status) => status.value === values.status)
          ?.label as string) || '';
      setCurrentStatus(currentStatus);
    },
  });

  useEffect(() => {
    const isValuable = query?.isValuable === '2';
    if (isValuable) {
      formik.setFieldValue('item_is_valuable', 2);
    }
  }, []);

  useEffect(() => {
    const currentStatus =
      (loseBasic?.lose_status?.find((status) => status.value === 5)?.label as string) || '';
    setCurrentStatus(currentStatus);
    formik.setFieldValue('status', 5);
    getLoseList({ page: 1, ...formik.values, status: 5 });
  }, [loseBasic.lose_status]);

  const loading = useLoading(loseLoading || loseListLoading);

  return (
    <>
      {loading && <PageLoader />}
      <Panel
        leftShadow
        style={{
          height: 'calc(100vh - var(--headerHeight) - var(--navBarHeight) - var(--toolBarHeight))',
        }}
      >
        <FormikProvider value={formik}>
          <Form>
            <Card horizontal className="h-[80px] flex-none">
              <div className="inline-grid grid-cols-5" style={{ width: 'calc(100% - 40px)' }}>
                <Card.Picker
                  className="mr-[28px]"
                  placeholder="选择服务状态"
                  formKey="status"
                  cleanable={false}
                  pickerData={[{ label: '全部状态', value: 0 }, ...loseBasic.lose_status]}
                />
                <Card.Input className="mr-[28px]" placeholder="输入服务编号" formKey="service_no" />
                <Card.Input className="mr-[28px]" placeholder="输入物品编号" formKey="item_no" />
                <Card.Input
                  className="mr-[28px]"
                  placeholder="输入物品关键词"
                  formKey="item_content"
                />
                <Card.Input
                  className="mr-[14px]"
                  placeholder="输入挂失人会员号"
                  formKey="user_code"
                />
                <Card.Input className="mr-[28px]" placeholder="输入挂失人手机号" formKey="phone" />
                <Card.DateRangePicker
                  className="mr-[28px]"
                  placeholder="拾到时间"
                  formKey="time_range"
                  format={BICESTER_FULL_DATE}
                />
              </div>
              <IconButton type="submit" className="flex items-center">
                <Image src="/resources/images/search-btn.svg" />
              </IconButton>
            </Card>
            <div className="flex justify-between items-center h-[60px]">
              <div className="text-[20px]">{currentStatus}</div>
              <div className="flex items-center">
                <IconButton
                  className="flex items-center mr-[50px]"
                  onClick={() =>
                    formik.setFieldValue(
                      'item_is_valuable',
                      formik.values?.item_is_valuable === 2 ? 1 : 2
                    )
                  }
                >
                  <Image
                    className="w-[18px] h-[18px]"
                    src={`/resources/images/checkbox${
                      formik.values?.item_is_valuable === 2 ? '-checked-fill' : ''
                    }.svg`}
                  />
                  <div className="text-[16px] text-[#999999] ml-[5px]">高额物品</div>
                </IconButton>
                <IconButton
                  className="flex items-center"
                  onClick={() => push('/site-service/l-and-f/create')}
                >
                  <Image src="/resources/images/add.svg" />
                  <div className="text-[16px] text-[#999999] ml-[5px]">新增失物招领</div>
                </IconButton>
              </div>
            </div>
          </Form>
        </FormikProvider>
        <Table
          style={{
            height:
              useContentHeight({
                withToolBar: true,
                withPanelPaddingTop: true,
                withPanelPaddingBottom: true,
                withTablePagination: true,
              }) - 140,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
          loading={loseListLoading}
          pageInfo={{ page: loseListData?.current_page, total: loseListData?.total }}
          data={loseListData?.content_list ?? []}
          columns={[
            {
              columnProps: {
                flexGrow: 2,
                sortable: true,
              },
              header: '服务编号',
              cell: <Cell dataKey="service_no" />,
            },
            {
              columnProps: {
                flexGrow: 2,
              },
              header: '服务状态',
              cell: <StatusCell dataKey="lose_status" statusOptionsData={loseBasic.lose_status} />,
            },
            {
              columnProps: {
                flexGrow: 3,
                sortable: true,
              },
              header: '拾到时间',
              cell: (
                <DateTimeCell dataKey="pick_date_at" format={BICESTER_FULL_DATE_HOURS_MINUTES} />
              ),
            },
            {
              columnProps: {
                flexGrow: 2,
              },
              header: '物品编号',
              cell: <Cell dataKey="item_no" />,
            },
            {
              columnProps: {
                flexGrow: 1.5,
              },
              header: '物品类别',
              cell: <Cell dataKey="item_type" />,
            },
            {
              columnProps: {
                flexGrow: 2.5,
              },
              header: '物品描述',
              cell: <Cell dataKey="item_remark" />,
            },
            {
              columnProps: {
                flexGrow: 2,
              },
              header: '挂失人/领取人',
              cell: <Cell dataKey="user_name" />,
            },
            {
              columnProps: {
                flexGrow: 1,
              },
              header: '记录详情',
              cell: (
                <OperationCell
                  onClick={(rowData) => {
                    const { id } = rowData;
                    window.open(getPathname(`/site-service/record/info/l-and-f/${id}`));
                  }}
                />
              ),
            },
          ]}
          onChange={onChange}
        >
          <Table.Body
            sortColumn={sortColumn}
            sortType={orderType}
            onSortColumn={(orderColumn: string, orderType: 'asc' | 'desc') => {
              setSortColumn(orderColumn);
              setOrderType(orderType);
            }}
            height={
              useContentHeight({
                withToolBar: true,
                withPanelPaddingTop: true,
                withPanelPaddingBottom: true,
                withTablePagination: true,
              }) - 140
            }
          />
          <Table.Pagination />
        </Table>
      </Panel>
    </>
  );
};

export default LostAndFoundList;
